import { useAuth } from 'auth/AuthContext'
import { AdminTabs } from 'components/AdminTabs'
import { useConfirm } from 'components/Confirm'
import { Button, Card, EmptyState, ErrorState, Spinner } from 'components/ui'
import { ROLE_LABELS, formatDateTime } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

import * as api from 'api/api'

/** Cross-project pending invitations, admin-only (Figma: Admin / 20 · Users → Invitations tab). Reuses the same
 *  Invitation entity and createInvitation/revokeInvitation mutations already built for per-project Settings. */
export const InvitationsPage = () => {
  const { user } = useAuth()
  const { data, error, loading, reload, setData } = useAsync(api.fetchAllInvitations, [])
  const { busy, run } = useAction()
  const confirm = useConfirm()

  if (!user?.isAdmin) return <ForbiddenPage message={t('إدارة الدعوات متاحة لمدير النظام فقط.', 'Invitations are available to Admin only.')} />

  const revoke = async (id: string) => {
    if (!(await confirm(t('إلغاء هذه الدعوة؟', 'Cancel this invitation?'), { confirmLabel: t('إلغاء الدعوة', 'Cancel invitation') }))) return
    const done = await run(`rv${id}`, () => api.revokeInvitation(id), t('تم إلغاء الدعوة', 'Invitation cancelled'))
    if (done) setData((current) => current?.filter((item) => item.id !== id))
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('المستخدمون والأدوار', 'Users and roles')}</h1>
          <p className="muted">{t('الدعوات المُرسلة التي لم تُقبل بعد، عبر كل المشاريع', 'Sent invitations that have not been accepted yet, across every project')}</p>
        </div>
      </div>

      <AdminTabs pendingInvitations={data?.length} />

      <Card title={t(`الدعوات المعلّقة (${data?.length ?? 0})`, `Pending invitations (${data?.length ?? 0})`)}>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !data?.length ? (
          <EmptyState title={t('لا توجد دعوات معلّقة', 'No pending invitations')} icon="file" />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('البريد', 'Email')}</th>
                  <th scope="col">{t('المشروع', 'Project')}</th>
                  <th scope="col">{t('الدور', 'Role')}</th>
                  <th scope="col">{t('المُرسِل', 'Sent by')}</th>
                  <th scope="col">{t('تنتهي في', 'Expires')}</th>
                  <th scope="col">
                    <span className="sr-only">{t('إجراءات', 'Actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((invitation) => (
                  <tr key={invitation.id}>
                    <td dir="ltr" className="ltr-cell">
                      {invitation.email}
                    </td>
                    <td>{invitation.projectName}</td>
                    <td>{ROLE_LABELS[invitation.role]}</td>
                    <td>{invitation.invitedByName}</td>
                    <td>{formatDateTime(invitation.expiresAt)}</td>
                    <td>
                      <Button size="sm" variant="ghost" busy={busy === `rv${invitation.id}`} onClick={() => revoke(invitation.id)}>
                        {t('إلغاء', 'Cancel')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
